const SCHOOL_ALIASES = {
    'mountainside': 'mma',
    'mountainside music academy': 'mma',
    'lakeside': 'lsma',
    'lakeside music academy': 'lsma',
    'treeside': 'tma',
    'treeside music academy': 'tma',
    'morningside': 'mor',
    'morningside music academy': 'mor',
    'parkside': 'psma',
    'parkside music academy': 'psma',
    'denton': 'den',
    'denton music academy': 'den',
    'tempe': 'tem',
    'tempe music academy': 'tem',
    'tulsa': 'tul',
    'tulsa music academy': 'tul',
}

const UNASSIGNABLE_STATUSES = new Set(['leaving', 'left school'])

export const UNASSIGNED_TEACHER_FILTER_ID = '__unassigned__'

export const normalizeTeacherAssignmentSchool = (school) => {
    const normalizedSchool = String(school || '').trim().toLowerCase()
    return SCHOOL_ALIASES[normalizedSchool] || normalizedSchool
}

export const isTeacherAvailableForAssignment = (teacher) => {
    const status = String(teacher?.Status || '').trim().toLowerCase()
    return Boolean(teacher?.TeacherID) && !UNASSIGNABLE_STATUSES.has(status)
}

export const getAssignableTeachersForCard = (teachers, card) => {
    const cardSchool = normalizeTeacherAssignmentSchool(card?.whichSchool)

    if (!cardSchool || !Array.isArray(teachers)) {
        return []
    }

    return teachers
        .filter((teacher) =>
            isTeacherAvailableForAssignment(teacher)
            && normalizeTeacherAssignmentSchool(teacher?.School) === cardSchool
        )
        .sort((teacherA, teacherB) =>
            String(teacherA?.Name || '').localeCompare(
                String(teacherB?.Name || ''),
                undefined,
                { sensitivity: 'base' }
            )
        )
}

export const getAssignedTeachersForCard = (card, teachers) => {
    const savedTeachers = Array.isArray(card?.assignedTeachers)
        ? card.assignedTeachers
        : []

    return savedTeachers
        .map((savedTeacher) => {
            const assignedTeacherId = String(savedTeacher?.teacherId || '').trim()
            if (!assignedTeacherId) return null

            const currentTeacher = Array.isArray(teachers)
                ? teachers.find(
                    (teacher) =>
                        String(teacher?.TeacherID || '').trim() === assignedTeacherId
                )
                : null

            if (!currentTeacher) {
                return savedTeacher
            }

            return {
                teacherId: currentTeacher.TeacherID,
                teacherName: currentTeacher.Name,
                imageName: currentTeacher.ImageName,
                school: currentTeacher.School,
                status: currentTeacher.Status,
            }
        })
        .filter(Boolean)
}

export const getAssignedTeacherIdsForCard = (card) => (
    (Array.isArray(card?.assignedTeachers) ? card.assignedTeachers : [])
        .map((teacher) => String(
            teacher?.teacherId
            || teacher?.TeacherID
            || teacher?.id
            || ''
        ).trim())
        .filter(Boolean)
)

export const matchesTeacherAssignmentFilter = (card, selectedTeacherIds) => {
    const selectedIds = Array.isArray(selectedTeacherIds)
        ? selectedTeacherIds.map((teacherId) => String(teacherId || '').trim()).filter(Boolean)
        : []

    if (selectedIds.length === 0) return true
    if (card?.whichList === 'teacher') return false

    const assignedTeacherIds = new Set(getAssignedTeacherIdsForCard(card))
    const includesUnassigned = selectedIds.includes(UNASSIGNED_TEACHER_FILTER_ID)

    if (includesUnassigned && assignedTeacherIds.size === 0) return true

    return selectedIds.some((teacherId) => assignedTeacherIds.has(teacherId))
}

export const buildTeacherAssignmentFilterOptions = ({
    teachers = [],
    cards = [],
    schoolKey = '',
} = {}) => {
    const normalizedSchoolFilter = normalizeTeacherAssignmentSchool(schoolKey)
    const teacherById = new Map()

    ;(Array.isArray(cards) ? cards : []).forEach((card) => {
        if (card?.whichList === 'teacher') return

        ;(Array.isArray(card?.assignedTeachers) ? card.assignedTeachers : []).forEach(
            (teacher) => {
                const teacherId = String(
                    teacher?.teacherId || teacher?.TeacherID || teacher?.id || ''
                ).trim()
                const teacherSchool = normalizeTeacherAssignmentSchool(
                    teacher?.school || teacher?.School || card?.whichSchool
                )

                if (
                    !teacherId
                    || (normalizedSchoolFilter && teacherSchool !== normalizedSchoolFilter)
                ) return

                teacherById.set(teacherId, {
                    teacherId,
                    teacherName: String(
                        teacher?.teacherName || teacher?.Name || 'Assigned teacher'
                    ).trim(),
                    school: teacherSchool,
                })
            }
        )
    })

    ;(Array.isArray(teachers) ? teachers : []).forEach((teacher) => {
        if (!isTeacherAvailableForAssignment(teacher)) return

        const teacherId = String(teacher?.TeacherID || '').trim()
        const teacherSchool = normalizeTeacherAssignmentSchool(teacher?.School)
        if (
            normalizedSchoolFilter
            && teacherSchool !== normalizedSchoolFilter
        ) return

        teacherById.set(teacherId, {
            teacherId,
            teacherName: String(teacher?.Name || 'Teacher').trim(),
            school: teacherSchool,
        })
    })

    const teacherOptions = Array.from(teacherById.values())
        .sort((teacherA, teacherB) => (
            teacherA.teacherName.localeCompare(
                teacherB.teacherName,
                undefined,
                { sensitivity: 'base' }
            )
        ))
        .map((teacher) => ({
            id: teacher.teacherId,
            label: teacher.teacherName,
        }))

    return [
        { id: UNASSIGNED_TEACHER_FILTER_ID, label: 'Unassigned cards' },
        ...teacherOptions,
    ]
}

export const resolveTeacherImageUrl = (imageName) => {
    const normalizedImageName = String(imageName || '').trim()

    if (!normalizedImageName) {
        return ''
    }

    if (/^(https?:|data:|blob:)/i.test(normalizedImageName)) {
        return normalizedImageName
    }

    return `https://app.atharo.app/images/${normalizedImageName}`
}
